import { useState, useEffect, useMemo, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { getCurrentUserCompanyId } from '@/lib/userUtils';
import { useToast } from '@/hooks/use-toast';
import { 
  CheckCircle2, 
  ChevronLeft, 
  ChevronRight,
  Loader2,
  ArrowRight,
  Brain,
  Eye,
  EyeOff,
  Layers,
  Sparkles,
  Package,
  TrendingUp,
  TrendingDown,
  Store,
  List,
  Tag,
  Info,
  Split
} from 'lucide-react';
import { IngredientSearchCombobox } from './IngredientSearchCombobox';
import { cn } from '@/lib/utils';
import { convertUnits, STANDARD_UNIT_OPTIONS } from '@/lib/unitConversions';

interface COGSVendorTrainingStep5Props {
  onNext: () => void;
  onPrevious: () => void;
  selectedVendors: string[];
  onEditMapping?: (vendorId: string, clusterId?: string) => void;
}

interface LineItem {
  id: string;
  cluster_id?: string;
  vendor_id: string;
  vendor_name: string;
  receipt_item_name: string; // From invoice
  invoice_id?: string;
  invoice_file_name?: string;
  invoice_date?: string;
  purchase_qty?: number;
  purchase_unit?: string;
  purchase_price?: number;
  category?: string;
  inventory_item_id?: string;
  inventory_item_name?: string;
  recipe_unit?: string;
  cost_per_recipe_unit?: number;
  is_trained?: boolean;
}

interface LineItemGroup {
  descriptionPattern: string;
  lineItems: LineItem[];
  isTrained: boolean;
  vendorId: string;
  vendorName: string;
}

// Unit options for dropdowns - using shared utility to avoid duplicates
const UNIT_OPTIONS = STANDARD_UNIT_OPTIONS;

const CATEGORY_OPTIONS = [
  { value: 'Food Cost', label: 'Food Cost' },
  { value: 'Packaging', label: 'Packaging' },
  { value: 'Supplies', label: 'Supplies' },
  { value: 'Non-Business', label: 'Non-Business' },
];

/**
 * Calculates similarity between two product names using normalized comparison
 * Uses a combination of exact match, substring match, and fuzzy matching
 * 
 * Bug Fix: Improved clustering for items like "Coke 12oz" variations by:
 * 1. Extracting core product names (brand + product type)
 * 2. Adding Levenshtein-based similarity for short strings
 * 3. Lowering word length threshold from 3 to 2 characters
 */
const calculateProductSimilarity = (name1: string, name2: string): number => {
  const normalize = (s: string) => s
    .toLowerCase()
    // Remove size variants (RS, SM, LG, MD, etc.) that shouldn't split groups
    .replace(/\b(rs|sm|md|lg|xl|xxl|regular|small|medium|large)\b/gi, '')
    // Remove package indicators
    .replace(/\b(cs|bx|pk|case|box|pack|ct|count|ea|each|per)\b/gi, '')
    // Remove size indicators like "12oz", "5lb", "20oz", etc.
    .replace(/\b\d+\s*(oz|lb|lbs|g|kg|ml|l|gal|pt|qt)\b/gi, '')
    // Remove standalone numbers (like pack counts)
    .replace(/\b\d+\b/g, '')
    // Remove color variants that shouldn't split groups (BLK, WHT, etc.)
    .replace(/\b(blk|wht|red|blu|grn|ylw|org|prp|pnk|brn|gry|black|white|blue|green|yellow|orange|purple|pink|brown|gray|grey)\b/gi, '')
    // Keep alphanumeric and spaces
    .replace(/[^a-z0-9\s-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  const n1 = normalize(name1);
  const n2 = normalize(name2);
  
  // Exact match after normalization
  if (n1 === n2) return 1.0;
  if (!n1 || !n2) return 0;
  
  // Check for significant substring match (one contains the other)
  if (n1.length >= 3 && n2.length >= 3) {
    if (n1.includes(n2) || n2.includes(n1)) return 0.95;
  }
  
  // Extract product codes (e.g., "FF3-8", "FF 3/8")
  const extractProductCode = (s: string): string | null => {
    const match = s.match(/\b([a-z]{1,4})\s*[-/]?\s*(\d{1,3})\s*[-/]?\s*(\d{0,3})\b/i);
    if (match) {
      return (match[1] + match[2] + (match[3] || '')).toUpperCase();
    }
    return null;
  };
  
  const code1 = extractProductCode(name1);
  const code2 = extractProductCode(name2);
  if (code1 && code2 && code1 === code2) return 0.95;
  
  // Extract core product name (first significant word - usually brand name)
  const extractCoreName = (s: string): string => {
    const words = s.split(' ').filter(w => w.length >= 2);
    // Return first 1-2 significant words as core identifier
    return words.slice(0, 2).join(' ');
  };
  
  const core1 = extractCoreName(n1);
  const core2 = extractCoreName(n2);
  
  // If core names match exactly, high similarity
  if (core1 && core2 && core1 === core2) return 0.92;
  
  // Levenshtein distance for short strings (good for typos and minor variations)
  const levenshteinDistance = (a: string, b: string): number => {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;
    
    const matrix: number[][] = [];
    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[b.length][a.length];
  };
  
  // Calculate Levenshtein similarity for normalized names
  const maxLen = Math.max(n1.length, n2.length);
  if (maxLen > 0) {
    const distance = levenshteinDistance(n1, n2);
    const levenshteinSimilarity = 1 - (distance / maxLen);
    // If very similar by Levenshtein (>80%), consider them the same
    if (levenshteinSimilarity >= 0.8) return levenshteinSimilarity;
  }
  
  // Word-based Jaccard similarity (lowered threshold from 3 to 2 chars)
  const words1 = new Set(n1.split(' ').filter(w => w.length >= 2));
  const words2 = new Set(n2.split(' ').filter(w => w.length >= 2));
  const intersection = new Set([...words1].filter(x => words2.has(x)));
  const union = new Set([...words1, ...words2]);
  
  if (union.size === 0) return 0;
  
  // Weighted Jaccard: give more weight if core words match
  const jaccardSimilarity = intersection.size / union.size;
  
  // Boost similarity if first word (usually brand/product name) matches
  const firstWord1 = n1.split(' ')[0];
  const firstWord2 = n2.split(' ')[0];
  if (firstWord1 && firstWord2 && firstWord1 === firstWord2 && firstWord1.length >= 3) {
    return Math.min(1.0, jaccardSimilarity + 0.2);
  }
  
  return jaccardSimilarity;
};

/**
 * Groups similar line items using similarity threshold (ID 3: 75% similarity threshold)
 * This allows items like "FF 3/8 STRAIGHT" and "FF 3/8 STRAIGHT RS" to be grouped together
 */
const groupLineItems = (lineItems: LineItem[], similarityThreshold: number = 0.75): LineItemGroup[] => {
  if (lineItems.length === 0) return [];
  
  const groups: LineItemGroup[] = [];
  const processed = new Set<number>();
  
  // Group by vendor first, then by similarity
  const byVendor = new Map<string, LineItem[]>();
  lineItems.forEach(item => {
    if (!byVendor.has(item.vendor_id)) {
      byVendor.set(item.vendor_id, []);
    }
    byVendor.get(item.vendor_id)!.push(item);
  });
  
  for (const [vendorId, vendorItems] of byVendor) {
    const vendorProcessed = new Set<number>();
    
    for (let i = 0; i < vendorItems.length; i++) {
      if (vendorProcessed.has(i)) continue;
      
      const currentItem = vendorItems[i];
      const cluster: LineItem[] = [currentItem];
      vendorProcessed.add(i);
      
      // Find similar items within same vendor
      for (let j = i + 1; j < vendorItems.length; j++) {
        if (vendorProcessed.has(j)) continue;
        
        const similarity = calculateProductSimilarity(
          currentItem.receipt_item_name,
          vendorItems[j].receipt_item_name
        );
        
        if (similarity >= similarityThreshold) {
          cluster.push(vendorItems[j]);
          vendorProcessed.add(j);
        }
      }
      
      // Use the most common/longest name as the pattern
      const patternName = cluster.reduce((longest, item) => 
        item.receipt_item_name.length > longest.length ? item.receipt_item_name : longest,
        cluster[0].receipt_item_name
      );
      
      groups.push({
        descriptionPattern: `${vendorId}-${patternName}`,
        lineItems: cluster,
        isTrained: cluster.some(item => item.is_trained),
        vendorId: currentItem.vendor_id,
        vendorName: currentItem.vendor_name,
      });
    }
  }
  
  return groups;
};

export const COGSVendorTrainingStep5 = ({ onNext, onPrevious, selectedVendors, onEditMapping }: COGSVendorTrainingStep5Props) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lineItemGroups, setLineItemGroups] = useState<LineItemGroup[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showSimilar, setShowSimilar] = useState(false);
  const [ungroupMode, setUngroupMode] = useState(false); // ID 6: Align with Bank module UX
  const [selectedItemsToSplit, setSelectedItemsToSplit] = useState<Set<string>>(new Set());
  const [isSplitting, setIsSplitting] = useState(false);
  
  // Form state for current line item
  const [inventoryItemId, setInventoryItemId] = useState<string>('');
  const [inventoryItemName, setInventoryItemName] = useState<string>('');
  const [category, setCategory] = useState<string>('');
  const [purchaseQty, setPurchaseQty] = useState<string>('');
  const [purchaseUnit, setPurchaseUnit] = useState<string>('');
  const [purchaseUnitCustom, setPurchaseUnitCustom] = useState<string>('');
  const [recipeUnit, setRecipeUnit] = useState<string>('');
  const [recipeUnitCustom, setRecipeUnitCustom] = useState<string>('');
  const [isCoupon, setIsCoupon] = useState<boolean>(false);
  const [priceAggregation, setPriceAggregation] = useState<'average' | 'most_recent' | 'lowest' | 'highest'>('most_recent');
  
  const [dataLoaded, setDataLoaded] = useState(false);

  const loadingCounterRef = useRef(0);
  const pendingNavToNextRef = useRef(false);

  useEffect(() => {
    loadLineItems();
  }, [selectedVendors]);

  // Load existing mapping when currentIndex changes or when groups are loaded
  useEffect(() => {
    if (currentGroup && currentItem && dataLoaded) {
      loadExistingMapping();
    }
  }, [currentIndex, dataLoaded, lineItemGroups.length]);

  // Navigate to next untrained after reload (used after handleSave to avoid stale closure)
  useEffect(() => {
    if (pendingNavToNextRef.current && dataLoaded && lineItemGroups.length > 0) {
      pendingNavToNextRef.current = false;
      goToNextUntrained(lineItemGroups);
    }
  }, [lineItemGroups, dataLoaded]);

  // Load existing mapping for current group
  const loadExistingMapping = async () => {
    if (!currentGroup || !currentItem) return;

    const requestId = ++loadingCounterRef.current;

    // Reset form immediately to prevent stale data from previous group showing
    resetFormState();

    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) return;
      if (requestId !== loadingCounterRef.current) return; // navigated away, stale request

      // Try to find mapping by cluster_id first, then by vendor_product_description
      let mapping = null;
      
      if (currentItem.cluster_id) {
        const { data, error: clusterError } = await supabase
          .from('vendor_ingredient_mappings')
          .select('*')
          .eq('company_id', companyId)
          .eq('vendor_id', currentItem.vendor_id)
          .eq('cluster_id', currentItem.cluster_id)
          .maybeSingle();
        
        if (clusterError && clusterError.code !== 'PGRST116') {
          throw clusterError;
        }
        mapping = data;
      }
      
      // If no cluster mapping found, try by description
      if (!mapping) {
        const { data, error: descError } = await supabase
          .from('vendor_ingredient_mappings')
          .select('*')
          .eq('company_id', companyId)
          .eq('vendor_id', currentItem.vendor_id)
          .eq('vendor_product_description', currentItem.receipt_item_name)
          .maybeSingle();
        
        if (descError && descError.code !== 'PGRST116') {
          throw descError;
        }
        mapping = data;
      }

      if (mapping) {
        if (requestId !== loadingCounterRef.current) return;
        setCategory(mapping.category || '');
        setInventoryItemId(mapping.ingredient_id || '');
        
        // Load ingredient name if ingredient_id exists
        if (mapping.ingredient_id) {
          const { data: ingredient } = await supabase
            .from('ingredients')
            .select('name')
            .eq('id', mapping.ingredient_id)
            .maybeSingle();
          setInventoryItemName(ingredient?.name || '');
        } else {
          setInventoryItemName('');
        }
        
        setPurchaseQty(String(mapping.purchase_qty || currentItem.purchase_qty || ''));
        setPurchaseUnit(mapping.purchase_unit || currentItem.purchase_unit || '');
        setPurchaseUnitCustom(''); // Reset custom unit
        setRecipeUnit(mapping.recipe_unit || '');
        setRecipeUnitCustom(''); // Reset custom unit
        setIsCoupon(mapping.is_coupon || false);
        setPriceAggregation('most_recent'); // Default to most_recent (column not stored in DB)
      } else {
        // No existing mapping, reset to defaults from line item
        if (requestId !== loadingCounterRef.current) return;
        setPurchaseQty(String(currentItem.purchase_qty || ''));
        setPurchaseUnit(currentItem.purchase_unit || '');
        setPurchaseUnitCustom('');
        setCategory('');
        setInventoryItemId('');
        setInventoryItemName('');
        setRecipeUnit('');
        setRecipeUnitCustom('');
        setIsCoupon((currentItem.purchase_price || 0) <= 0);
        setPriceAggregation('most_recent');
      }
    } catch (error) {
      console.error('Error loading existing mapping:', error);
      // On error, just use defaults from line item
      if (requestId !== loadingCounterRef.current) return;
      setPurchaseQty(String(currentItem.purchase_qty || ''));
      setPurchaseUnit(currentItem.purchase_unit || '');
      setPurchaseUnitCustom('');
      setCategory('');
      setInventoryItemId('');
      setInventoryItemName('');
      setRecipeUnit('');
      setRecipeUnitCustom('');
      setIsCoupon((currentItem.purchase_price || 0) <= 0);
      setPriceAggregation('most_recent');
    }
  };

  const loadLineItems = async () => {
    try {
      setLoading(true);
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) {
        setLoading(false);
        setDataLoaded(true);
        return;
      }

      // Load all line items from invoices for selected vendors
      const { data: invoices, error: invoicesError } = await supabase
        .from('vendor_invoice_training')
        .select('id, vendor_id, invoice_date, invoice_file_name, line_items')
        .eq('company_id', companyId)
        .in('vendor_id', selectedVendors)
        .eq('ocr_status', 'completed')
        .not('line_items', 'is', null);

      if (invoicesError) throw invoicesError;

      // Load vendors for names
      const { data: vendors } = await supabase
        .from('vendors')
        .select('id, name')
        .in('id', selectedVendors);

      const vendorMap = new Map((vendors || []).map(v => [v.id, v.name]));

      // Load existing mappings to check which items are trained
      const { data: mappings } = await supabase
        .from('vendor_ingredient_mappings')
        .select('cluster_id, ingredient_id, category, purchase_qty, purchase_unit, recipe_unit, vendor_product_description, vendor_id')
        .eq('company_id', companyId)
        .in('vendor_id', selectedVendors);

      const trainedClusterIds = new Set((mappings || []).map(m => m.cluster_id).filter(Boolean));
      
      // Create a set of trained descriptions by vendor
      const trainedDescriptionsByVendor = new Map<string, Set<string>>();
      (mappings || []).forEach(m => {
        if (m.vendor_product_description && m.vendor_id) {
          const normalized = m.vendor_product_description.toLowerCase().trim();
          if (!trainedDescriptionsByVendor.has(m.vendor_id)) {
            trainedDescriptionsByVendor.set(m.vendor_id, new Set());
          }
          trainedDescriptionsByVendor.get(m.vendor_id)!.add(normalized);
        }
      });

      // Flatten line items from all invoices
      const allLineItems: LineItem[] = [];
      (invoices || []).forEach(invoice => {
        const lineItems = Array.isArray(invoice.line_items) ? invoice.line_items : [];
        lineItems.forEach((item: any, idx: number) => {
          // Skip summary items
          const desc = (item.description || item.name || '').toLowerCase();
          const summaryKeywords = ['sub-total', 'subtotal', 'tax', 'total', 'payment', 'balance'];
          if (summaryKeywords.some(kw => desc.includes(kw))) return;

          // Auto-skip zero-dollar items (coupons, promotions, discounts)
          const itemPrice = item.unit_price || item.price || 0;
          if (itemPrice <= 0) return;

          const receiptItemName = item.description || item.name || '';
          const normalizedDesc = receiptItemName.toLowerCase().trim();
          const vendorTrainedDescriptions = trainedDescriptionsByVendor.get(invoice.vendor_id) || new Set();
          
          // Check if this item is trained by description or cluster_id
          const isTrained = vendorTrainedDescriptions.has(normalizedDesc) || 
                          (item.cluster_id && trainedClusterIds.has(item.cluster_id));

          allLineItems.push({
            id: `${invoice.id}-${idx}`,
            vendor_id: invoice.vendor_id,
            vendor_name: vendorMap.get(invoice.vendor_id) || 'Unknown',
            receipt_item_name: receiptItemName,
            invoice_id: invoice.id,
            invoice_file_name: invoice.invoice_file_name,
            invoice_date: invoice.invoice_date,
            purchase_qty: item.quantity || item.qty || 1,
            purchase_unit: item.unit || '',
            purchase_price: item.unit_price || item.price || 0,
            is_trained: isTrained,
          });
        });
      });

      // Group similar line items
      const groups = groupLineItems(allLineItems);
      // Sort groups by number of line items (highest first) - ID 40
      const sortedGroups = groups.sort((a, b) => b.lineItems.length - a.lineItems.length);
      setLineItemGroups(sortedGroups);
      
      // Form initialization will be handled by useEffect when currentGroup is available

      setLoading(false);
      setDataLoaded(true);
    } catch (error: any) {
      console.error('Error loading line items:', error);
      toast({
        title: 'Error',
        description: 'Failed to load line items',
        variant: 'destructive',
      });
      setLoading(false);
      setDataLoaded(true);
    }
  };

  // Note: This function is used by the useMemo below which passes the aggregated price
  // Bug Fix: Accept price as parameter to use the aggregated purchase_price from useMemo
  const calculateCostPerRecipeUnit = (aggregatedPrice?: number): number => {
    if (!purchaseQty || !recipeUnit || !purchaseUnit) return 0;
    
    const qty = parseFloat(purchaseQty);
    // Use the passed aggregated price, falling back to currentItem price
    const price = aggregatedPrice ?? currentItem?.purchase_price ?? 0;
    const finalPurchaseUnit = purchaseUnitCustom || purchaseUnit;
    const finalRecipeUnit = recipeUnitCustom || recipeUnit;
    
    if (qty === 0 || price === 0) return 0;
    
    // Total price for all purchase units
    const totalCost = price;
    
    // Convert purchase quantity to recipe units
    const recipeUnitsPerPurchase = convertUnits(qty, finalPurchaseUnit, finalRecipeUnit);
    
    // Cost per recipe unit
    if (recipeUnitsPerPurchase === 0) return 0;
    return totalCost / recipeUnitsPerPurchase;
  };

  const handleSave = async () => {
    if (!category) {
      toast({
        title: 'Category Required',
        description: 'Please select a category for this line item',
        variant: 'destructive',
      });
      return;
    }

    // For Supplies / Non-business only Category, Item Name, and Total Price are required
    if (requiresRecipeCost && !purchaseQty) {
      toast({
        title: 'Purchase Quantity Required',
        description: 'Please enter the purchase quantity',
        variant: 'destructive',
      });
      return;
    }

    if (requiresRecipeCost && !recipeUnit && !recipeUnitCustom) {
      toast({
        title: 'Recipe Unit Required',
        description: 'Please select a recipe unit for Food Cost and Packaging items.',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      const currentGroup = lineItemGroups[currentIndex];
      const firstItem = currentGroup.lineItems[0];
      
      // Calculate cost per recipe unit using the aggregated purchase_price
      const finalPurchaseUnit = purchaseUnitCustom || purchaseUnit;
      // For Supplies/Non-business, default qty to 1 and unit to 'each' when not required
      const resolvedPurchaseQty = requiresRecipeCost ? parseFloat(purchaseQty) : (parseFloat(purchaseQty) || 1);
      const resolvedPurchaseUnit = requiresRecipeCost ? finalPurchaseUnit : (finalPurchaseUnit || 'each');
      const finalRecipeUnit = recipeUnitCustom || recipeUnit || resolvedPurchaseUnit;
      const savedCostPerRecipeUnit = requiresRecipeCost ? calculateCostPerRecipeUnit(purchase_price) : 0;

      const mappingData: any = {
        company_id: companyId,
        vendor_id: firstItem.vendor_id,
        cluster_id: firstItem.cluster_id || null,
        ingredient_id: inventoryItemId || null,
        vendor_product_description: firstItem.receipt_item_name,
        price_per_unit: purchase_price, // Use aggregated price (defaults to most_recent)
        unit_of_measure: resolvedPurchaseUnit,
        category: category,
        purchase_qty: resolvedPurchaseQty,
        purchase_unit: resolvedPurchaseUnit,
        recipe_unit: finalRecipeUnit,
        cost_per_recipe_unit: savedCostPerRecipeUnit,
        is_coupon: isCoupon,
      };

      // Check if mapping already exists by cluster_id or vendor_product_description
      let existingMapping = null;
      
      if (firstItem.cluster_id) {
        const { data, error: clusterError } = await supabase
          .from('vendor_ingredient_mappings')
          .select('id')
          .eq('company_id', companyId)
          .eq('vendor_id', firstItem.vendor_id)
          .eq('cluster_id', firstItem.cluster_id)
          .maybeSingle();
        
        if (clusterError && clusterError.code !== 'PGRST116') {
          throw clusterError;
        }
        existingMapping = data;
      }
      
      // If no cluster mapping found, check by vendor_product_description
      if (!existingMapping) {
        const { data, error: descError } = await supabase
          .from('vendor_ingredient_mappings')
          .select('id')
          .eq('company_id', companyId)
          .eq('vendor_id', firstItem.vendor_id)
          .eq('vendor_product_description', firstItem.receipt_item_name)
          .maybeSingle();
        
        if (descError && descError.code !== 'PGRST116') {
          throw descError;
        }
        existingMapping = data;
      }

      // Insert or update mapping
      if (existingMapping) {
        const { error: updateError } = await supabase
          .from('vendor_ingredient_mappings')
          .update(mappingData)
          .eq('id', existingMapping.id);
        
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('vendor_ingredient_mappings')
          .insert(mappingData);
        
        if (insertError) throw insertError;
      }

      // Sync ingredient's cost_per_standard_unit and standard_unit so downstream
      // features (prep items, menu costing) see the correct price and unit.
      if (inventoryItemId && requiresRecipeCost && savedCostPerRecipeUnit > 0) {
        await supabase
          .from('ingredients')
          .update({
            standard_unit: finalRecipeUnit,
            cost_per_standard_unit: savedCostPerRecipeUnit,
          })
          .eq('id', inventoryItemId)
          .eq('company_id', companyId);
      }

      // Reload ALL mappings for all selected vendors to update trained status consistently
      const { data: allMappings } = await supabase
        .from('vendor_ingredient_mappings')
        .select('cluster_id, vendor_product_description, vendor_id')
        .eq('company_id', companyId)
        .in('vendor_id', selectedVendors);
      
      if (!allMappings) throw new Error('Failed to load mappings after save');
      
      // Build per-vendor lookup for trained descriptions and cluster IDs
      const trainedClusterIds = new Set(allMappings.map(m => m.cluster_id).filter(Boolean));
      const trainedDescByVendor = new Map<string, Set<string>>();
      allMappings.forEach(m => {
        if (m.vendor_product_description && m.vendor_id) {
          if (!trainedDescByVendor.has(m.vendor_id)) {
            trainedDescByVendor.set(m.vendor_id, new Set());
          }
          trainedDescByVendor.get(m.vendor_id)!.add(m.vendor_product_description.toLowerCase().trim());
        }
      });
      
      const updatedGroups = lineItemGroups.map(g => {
        const firstItemInGroup = g.lineItems[0];
        const normalizedDesc = firstItemInGroup.receipt_item_name.toLowerCase().trim();
        const vendorDescs = trainedDescByVendor.get(firstItemInGroup.vendor_id);
        const isTrained = (firstItemInGroup.cluster_id && trainedClusterIds.has(firstItemInGroup.cluster_id)) ||
                         (vendorDescs?.has(normalizedDesc) ?? false);
        return { ...g, isTrained };
      });
      
      setLineItemGroups(updatedGroups);
      
      // Update training progress for the current vendor
      const trainedGroupsForVendor = updatedGroups.filter(
        g => g.isTrained && g.vendorId === firstItem.vendor_id
      ).length;
      
      const { error: progressError } = await supabase
        .from('vendor_training_progress')
        .upsert({
          company_id: companyId,
          vendor_id: firstItem.vendor_id,
          clusters_mapped: trainedGroupsForVendor,
          status: 'mapping',
        }, {
          onConflict: 'company_id,vendor_id',
        });
      
      if (progressError) {
        console.error('Error updating training progress:', progressError);
      }

      toast({
        title: 'Saved!',
        description: `Categorized ${currentGroup.lineItems.length} line item${currentGroup.lineItems.length > 1 ? 's' : ''}`,
      });

      resetFormState();
      pendingNavToNextRef.current = true;
      // lineItemGroups state was just set via setLineItemGroups(updatedGroups)
      // The useEffect above will fire after the state update and call goToNextUntrained

    } catch (error: any) {
      console.error('Error saving:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save mapping. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSplitGroup = async () => {
    if (selectedItemsToSplit.size === 0) {
      toast({
        title: 'No Items Selected',
        description: 'Please select at least one line item to split into a new group.',
        variant: 'destructive',
      });
      return;
    }

    if (selectedItemsToSplit.size >= currentGroup.lineItems.length) {
      toast({
        title: 'Invalid Selection',
        description: 'Cannot split all items. Please leave at least one item in the original group.',
        variant: 'destructive',
      });
      return;
    }

    setIsSplitting(true);
    try {
      const companyId = await getCurrentUserCompanyId();
      if (!companyId) throw new Error('Company ID not found');

      const itemsToSplit = currentGroup.lineItems.filter(item => selectedItemsToSplit.has(item.id));
      const itemsToKeep = currentGroup.lineItems.filter(item => !selectedItemsToSplit.has(item.id));

      if (itemsToKeep.length === 0) {
        toast({
          title: 'Invalid Selection',
          description: 'Must keep at least one item in the original group.',
          variant: 'destructive',
        });
        setIsSplitting(false);
        return;
      }

      // Create new group for split items
      const newGroup: LineItemGroup = {
        descriptionPattern: itemsToSplit[0].receipt_item_name,
        lineItems: itemsToSplit,
        isTrained: false,
        vendorId: currentGroup.vendorId,
        vendorName: currentGroup.vendorName,
      };

      // Update current group to keep remaining items
      const updatedCurrentGroup: LineItemGroup = {
        ...currentGroup,
        lineItems: itemsToKeep,
        descriptionPattern: itemsToKeep[0].receipt_item_name,
      };

      // Update groups array
      const updatedGroups = [...lineItemGroups];
      updatedGroups[currentIndex] = updatedCurrentGroup;
      updatedGroups.splice(currentIndex + 1, 0, newGroup);

      setLineItemGroups(updatedGroups);
      setSelectedItemsToSplit(new Set());
      setUngroupMode(false); // ID 6: Reset ungroup mode after split
      setShowSimilar(false);

      // Note: We don't update database clusters here as they are managed by the grouping function
      // The user will need to re-run grouping if they want to persist the split to the database
      // For now, the split is only in-memory for the current session

      toast({
        title: 'Group Split',
        description: `Ungrouped ${itemsToSplit.length} item(s) into a new group.`,
      });
    } catch (error: any) {
      console.error('Error splitting group:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to split group',
        variant: 'destructive',
      });
    } finally {
      setIsSplitting(false);
    }
  };

  const resetFormState = () => {
    setCategory('');
    setInventoryItemId('');
    setInventoryItemName('');
    setPurchaseQty('');
    setPurchaseUnit('');
    setPurchaseUnitCustom('');
    setRecipeUnit('');
    setRecipeUnitCustom('');
    setIsCoupon(false);
    setPriceAggregation('most_recent');
    setShowSimilar(false);
    setUngroupMode(false); // ID 6: Reset ungroup mode
    setSelectedItemsToSplit(new Set());
  };

  const goToNextUntrained = (groups: LineItemGroup[]) => {
    // Look ahead from current position
    let nextIndex = groups.findIndex((g, idx) => idx > currentIndex && !g.isTrained);
    
    // If not found after current, wrap around to check from beginning
    if (nextIndex === -1) {
      nextIndex = groups.findIndex((g, idx) => idx < currentIndex && !g.isTrained);
    }
    
    if (nextIndex !== -1) {
      resetFormState();
      setCurrentIndex(nextIndex);
    }
    // If no untrained found, the completion check (remainingGroups === 0) will show the done state
  };

  const goToPrevious = () => {
    if (currentIndex > 0) {
      resetFormState();
      setCurrentIndex(currentIndex - 1);
      // loadExistingMapping will be called by useEffect when currentIndex changes
    }
  };

  const goToNext = () => {
    if (currentIndex < lineItemGroups.length - 1) {
      resetFormState();
      setCurrentIndex(currentIndex + 1);
      // loadExistingMapping will be called by useEffect when currentIndex changes
    }
  };

  // Stats
  const totalGroups = lineItemGroups.length;
  const trainedGroups = lineItemGroups.filter(g => g.isTrained).length;
  const remainingGroups = totalGroups - trainedGroups;

  const currentGroup = lineItemGroups[currentIndex];
  const currentItem = currentGroup?.lineItems[0];
  const similarCount = currentGroup?.lineItems.length || 0;
  // Count unique receipts/invoices
  const uniqueReceipts = currentGroup 
    ? new Set(currentGroup.lineItems.map(item => item.invoice_file_name || item.invoice_id).filter(Boolean)).size 
    : 0;
  const requiresRecipeCost = category === 'Food Cost' || category === 'Packaging';
  // Calculate price statistics for the current group
  const priceStats = useMemo(() => {
    if (!currentGroup || currentGroup.lineItems.length === 0) {
      return { min: 0, max: 0, avg: 0, mostRecent: 0, prices: [] };
    }
    
    const prices = currentGroup.lineItems
      .map(item => item.purchase_price || 0)
      .filter(price => price > 0);
    
    if (prices.length === 0) {
      return { min: 0, max: 0, avg: 0, mostRecent: 0, prices: [] };
    }
    
    // Get most recent price (from item with latest invoice_date)
    const sortedByDate = [...currentGroup.lineItems].sort((a, b) => {
      const dateA = a.invoice_date ? new Date(a.invoice_date).getTime() : 0;
      const dateB = b.invoice_date ? new Date(b.invoice_date).getTime() : 0;
      return dateB - dateA; // Most recent first
    });
    const mostRecent = sortedByDate[0]?.purchase_price || 0;
    
    return {
      min: Math.min(...prices),
      max: Math.max(...prices),
      avg: prices.reduce((sum, p) => sum + p, 0) / prices.length,
      mostRecent,
      prices,
    };
  }, [currentGroup]);

  // Calculate aggregated price based on selected method
  const purchase_price = useMemo(() => {
    if (!priceStats.prices.length) return currentItem?.purchase_price || 0;
    
    switch (priceAggregation) {
      case 'average':
        return priceStats.avg;
      case 'most_recent':
        return priceStats.mostRecent;
      case 'lowest':
        return priceStats.min;
      case 'highest':
        return priceStats.max;
      default:
        return priceStats.mostRecent;
    }
  }, [priceStats, priceAggregation, currentItem]);

  // Bug Fix: Pass the aggregated purchase_price to ensure Cost per Recipe Unit uses the most recent price
  const costPerRecipeUnit = useMemo(() => calculateCostPerRecipeUnit(purchase_price), [purchaseQty, purchase_price, recipeUnit, purchaseUnit, purchaseUnitCustom, recipeUnitCustom]);

  // Loading state
  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-7 w-64" />
          <Skeleton className="h-4 w-96" />
        </div>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-64" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // No line items
  if (dataLoaded && lineItemGroups.length === 0) {
    return (
      <div className="space-y-6">
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="text-center py-8 space-y-4">
              <CheckCircle2 className="h-16 w-16 text-emerald-600 mx-auto" />
              <div>
                <h4 className="font-semibold mb-2 text-lg">All Caught Up!</h4>
                <p className="text-muted-foreground">No line items to train.</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="flex justify-between">
          <Button variant="outline" onClick={onPrevious}>← Previous Step</Button>
          <Button onClick={onNext}>
            Continue to Review
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  // All trained
  if (dataLoaded && remainingGroups === 0) {
    return (
      <div className="space-y-6">
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="pt-6">
            <div className="text-center py-8 space-y-4">
              <CheckCircle2 className="h-16 w-16 text-emerald-600 mx-auto" />
              <div>
                <h4 className="font-semibold mb-2 text-lg">Training Complete!</h4>
                <p className="text-muted-foreground">
                  You've categorized across {trainedGroups} ingredient groups.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="flex justify-between">
          <Button variant="outline" onClick={onPrevious}>← Previous Step</Button>
          <Button onClick={onNext}>
            Continue to Review
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  if (!currentGroup || !currentItem) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-xl font-semibold mb-2">Train AI on Your Invoice Line Items</h3>
        <p className="text-muted-foreground">
          Categorize one line item and the AI will learn to automatically categorize similar ones.
        </p>
      </div>

      {/* Progress Bar */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Brain className="h-5 w-5 text-blue-600" />
              <span className="font-medium">Training Progress</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {trainedGroups} of {totalGroups} trained • {remainingGroups} remaining
            </span>
          </div>
          <Progress value={(trainedGroups / totalGroups) * 100} className="h-2" />
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={goToPrevious}
          disabled={currentIndex === 0}
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous
        </Button>
        <span className="text-sm text-muted-foreground">
          Ingredient Group {currentIndex + 1} of {totalGroups}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={goToNext}
          disabled={currentIndex === totalGroups - 1}
        >
          Next
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Main Line Item Card */}
      <Card className={cn(
        "border-2 transition-colors",
        currentGroup.isTrained ? "border-emerald-300 bg-emerald-50/30" : "border-primary/30"
      )}>
        <CardHeader className="pb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5 text-primary" />
                <span className="text-2xl font-bold">
                  ${purchase_price.toFixed(2)}
                </span>
              </CardTitle>
              <CardDescription className="mt-1 flex items-center gap-2">
                <Badge variant="outline">
                  <Store className="h-3 w-3 mr-1" />
                  {currentItem.vendor_name}
                </Badge>
                {currentItem.invoice_date && (
                  <span className="text-muted-foreground">
                    {new Date(currentItem.invoice_date).toLocaleDateString()}
                  </span>
                )}
              </CardDescription>
            </div>
            {currentGroup.isTrained && (
              <Badge className="bg-emerald-500">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Trained
              </Badge>
            )}
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Receipt Item Name */}
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground uppercase tracking-wide">Receipt Item Name</Label>
            <div className="bg-muted/50 p-4 rounded-lg font-mono text-sm">
              {currentItem.receipt_item_name}
            </div>
            <p className="text-xs text-muted-foreground">Auto-recognized from invoice</p>
          </div>

          {/* Similar Items Alert */}
          {similarCount > 1 && (
            <Alert className="border-blue-200 bg-blue-50">
              <Layers className="h-4 w-4 text-blue-600" />
              <AlertDescription className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <span>
                    <strong className="text-blue-700">{similarCount} similar line items</strong>
                    {uniqueReceipts > 1 && (
                      <span className="text-blue-600"> from {uniqueReceipts} different receipts</span>
                    )}
                    {' '}match this ingredient group. 
                    Categorizing this one will apply to all of them!
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="ml-4"
                    onClick={() => {
                      setShowSimilar(!showSimilar);
                      // Reset ungroup mode when hiding - ID 6
                      if (showSimilar) {
                        setUngroupMode(false);
                        setSelectedItemsToSplit(new Set());
                      }
                    }}
                  >
                    {showSimilar ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                    {showSimilar ? 'Hide' : 'Show Similar'}
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {/* Similar Items Table - ID 6: Aligned with Bank module UX */}
          {showSimilar && similarCount > 1 && (
            <Card className="border-dashed">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm">
                    Similar Line Items ({similarCount})
                    {uniqueReceipts > 1 && (
                      <span className="text-muted-foreground font-normal ml-2">
                        from {uniqueReceipts} different receipts
                      </span>
                    )}
                  </CardTitle>
                  <Button
                    variant={ungroupMode ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      if (ungroupMode) {
                        // Cancel ungroup mode
                        setUngroupMode(false);
                        setSelectedItemsToSplit(new Set());
                      } else {
                        // Enter ungroup mode
                        setUngroupMode(true);
                      }
                    }}
                    className="h-7 text-xs"
                  >
                    <Split className="h-3 w-3 mr-1" />
                    {ungroupMode ? 'Cancel' : 'Ungroup'}
                  </Button>
                </div>
                {ungroupMode && (
                  <div className="flex items-center gap-2 mt-2">
                    <Button
                      size="sm"
                      onClick={handleSplitGroup}
                      disabled={selectedItemsToSplit.size === 0 || isSplitting || selectedItemsToSplit.size >= currentGroup.lineItems.length}
                      className="h-7 text-xs"
                    >
                      {isSplitting ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                          Splitting...
                        </>
                      ) : (
                        <>Ungroup Selected ({selectedItemsToSplit.size})</>
                      )}
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      Select items to separate into different groups
                    </span>
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="max-h-[300px] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        {ungroupMode && (
                          <th className="text-left p-2 w-[40px]">
                            <Checkbox
                              checked={selectedItemsToSplit.size === currentGroup.lineItems.length && currentGroup.lineItems.length > 0}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  setSelectedItemsToSplit(new Set(currentGroup.lineItems.map(item => item.id)));
                                } else {
                                  setSelectedItemsToSplit(new Set());
                                }
                              }}
                              disabled={currentGroup.lineItems.length <= 1}
                            />
                          </th>
                        )}
                        <th className="text-left p-2">Receipt</th>
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Price</th>
                        <th className="text-left p-2">Qty</th>
                        <th className="text-left p-2">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentGroup.lineItems.map((item) => (
                        <tr key={item.id} className="border-b">
                          {ungroupMode && (
                            <td className="p-2">
                              <Checkbox
                                checked={selectedItemsToSplit.has(item.id)}
                                onCheckedChange={(checked) => {
                                  const newSet = new Set(selectedItemsToSplit);
                                  if (checked) {
                                    newSet.add(item.id);
                                  } else {
                                    newSet.delete(item.id);
                                  }
                                  setSelectedItemsToSplit(newSet);
                                }}
                              />
                            </td>
                          )}
                          <td className="p-2 text-xs font-medium">
                            {item.invoice_file_name || 'N/A'}
                          </td>
                          <td className="p-2 text-xs">
                            {item.invoice_date ? new Date(item.invoice_date).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="p-2 font-mono">${(item.purchase_price || 0).toFixed(2)}</td>
                          <td className="p-2">{item.purchase_qty || 1}</td>
                          <td className="p-2">{item.receipt_item_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Training Form */}
          <div className="space-y-4 pt-4 border-t">
            {/* Row 1: Category (left), Inventory Item Name (right) */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Category */}
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger>
                    <div className="flex items-center gap-2 flex-1">
                      <List className="h-4 w-4 opacity-50" />
                      <SelectValue placeholder="Select category" />
                    </div>
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Inventory Item Name - Link to inventory, filtered by category */}
              <div className="space-y-2">
                <Label>
                  Inventory Item Name{requiresRecipeCost ? ' *' : ' (optional for Supplies / Non-Business)'}
                </Label>
                <IngredientSearchCombobox
                  value={inventoryItemId}
                  onValueChange={(id, name, metadata) => {
                    setInventoryItemId(id || '');
                    setInventoryItemName(name);
                    // Auto-populate from ingredient metadata
                    if (metadata?.category) {
                      // Map database category values to CATEGORY_OPTIONS format
                      const categoryLower = metadata.category.toLowerCase().trim();
                      let categoryValue = metadata.category; // Default to original value
                      
                      // Map common database values to CATEGORY_OPTIONS
                      if (categoryLower === 'food cost' || categoryLower === 'food' || categoryLower === 'beverage cost' || categoryLower === 'beverage' || categoryLower === 'drinks') {
                        categoryValue = 'Food Cost';
                      } else if (categoryLower === 'packaging') {
                        categoryValue = 'Packaging';
                      } else if (categoryLower === 'supplies') {
                        categoryValue = 'Supplies';
                      } else if (categoryLower === 'non-business' || categoryLower === 'nonbusiness') {
                        categoryValue = 'Non-Business';
                      } else {
                        // If it doesn't match, try to find it in CATEGORY_OPTIONS
                        const matchingOption = CATEGORY_OPTIONS.find(opt => 
                          opt.value.toLowerCase() === categoryLower || 
                          opt.label.toLowerCase() === categoryLower
                        );
                        if (matchingOption) {
                          categoryValue = matchingOption.value;
                        }
                      }
                      setCategory(categoryValue);
                    }
                    if (metadata?.standardUnit) {
                      setRecipeUnit(metadata.standardUnit);
                    }
                    if (metadata?.purchaseUnit) {
                      setPurchaseUnit(metadata.purchaseUnit);
                    }
                  }}
                  category={category || undefined}
                  standardUnit={recipeUnit}
                  placeholder="Search or create ingredient..."
                />
                <p className="text-xs text-muted-foreground">
                  Link to inventory list from previous module.
                  {!requiresRecipeCost && ' Not required for Supplies or Non-Business categories.'}
                </p>
              </div>
            </div>

            {/* Row 2: For Food Cost/Packaging show Qty + Unit + Price (unit-level breakdown). For Supplies/Non-Business only show Price (overall spend). */}
            <div className={cn(
              'grid gap-4',
              requiresRecipeCost ? 'grid-cols-1 md:grid-cols-[180px_180px_1fr]' : 'grid-cols-1'
            )}>
              {requiresRecipeCost && (
                <>
                  {/* Purchase Qty - only for unit-level breakdown (Food Cost / Packaging) */}
                  <div className="space-y-2">
                    <Label>Purchase Qty *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={purchaseQty}
                      onChange={(e) => setPurchaseQty(e.target.value)}
                      placeholder="Enter quantity"
                      className="h-9"
                    />
                    <p className="text-xs text-muted-foreground">Manual entry</p>
                  </div>

                  {/* Purchase Unit - only for unit-level breakdown (Food Cost / Packaging) */}
                  <div className="space-y-2">
                    <Label>Purchase Unit *</Label>
                    <div className="space-y-2">
                      <Select value={purchaseUnit} onValueChange={(v) => {
                        setPurchaseUnit(v);
                        if (v !== 'custom') setPurchaseUnitCustom('');
                      }}>
                        <SelectTrigger className="h-9">
                          <div className="flex items-center gap-2 flex-1">
                            <Tag className="h-4 w-4 opacity-50" />
                            <SelectValue placeholder="Select unit" />
                          </div>
                        </SelectTrigger>
                        <SelectContent>
                          {UNIT_OPTIONS.map(unit => (
                            <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                          ))}
                          <SelectItem value="custom">Custom...</SelectItem>
                        </SelectContent>
                      </Select>
                      {purchaseUnit === 'custom' && (
                        <Input
                          placeholder="Enter custom unit"
                          value={purchaseUnitCustom}
                          onChange={(e) => setPurchaseUnitCustom(e.target.value)}
                          className="h-9"
                        />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">Unit dropdown selection with manual entry option</p>
                  </div>
                </>
              )}

              {/* Purchase Price - overall spend (all categories); from most recent invoice */}
              <div className="space-y-2">
                <Label>Purchase Price</Label>
                <div className="bg-muted/50 p-2 rounded-lg h-9 flex items-center">
                  <span className="font-mono font-medium">${purchase_price.toFixed(2)}</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  {requiresRecipeCost ? 'Price from the most recent invoice' : 'Total spend from the most recent invoice'}
                </p>
              </div>
            </div>

            {/* Row 3: Recipe Unit, Cost per Recipe Unit (only for Food Cost / Packaging) */}
            {requiresRecipeCost && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Recipe Unit */}
              <div className="space-y-2">
                <Label>Recipe Unit</Label>
                <div className="space-y-2">
                  <Select value={recipeUnit} onValueChange={(v) => {
                    setRecipeUnit(v);
                    if (v !== 'custom') setRecipeUnitCustom('');
                  }}>
                    <SelectTrigger>
                      <div className="flex items-center gap-2 flex-1">
                        <Info className="h-4 w-4 opacity-50" />
                        <SelectValue placeholder="Select unit" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {UNIT_OPTIONS.map(unit => (
                        <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                      ))}
                      <SelectItem value="custom">Custom...</SelectItem>
                    </SelectContent>
                  </Select>
                  {recipeUnit === 'custom' && (
                    <Input
                      placeholder="Enter custom unit"
                      value={recipeUnitCustom}
                      onChange={(e) => setRecipeUnitCustom(e.target.value)}
                    />
                  )}
                </div>
                <p className="text-xs text-muted-foreground">Unit dropdown selection with manual entry option</p>
              </div>

              {/* Cost per Recipe Unit - Auto-calculated */}
              <div className="space-y-2">
                <Label>Cost per Recipe Unit</Label>
                <div className="bg-primary/5 p-3 rounded-lg border border-primary/20">
                  <span className="font-mono font-bold text-lg">${costPerRecipeUnit.toFixed(2)}</span>
                </div>
                <p className="text-xs text-muted-foreground">Auto-calculated based on unit conversions</p>
              </div>
            </div>
            )}

            {/* Coupon Indicator - ID 38 */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2 p-3 border rounded-lg bg-muted/50">
                <Checkbox
                  id="is-coupon"
                  checked={isCoupon}
                  onCheckedChange={(checked) => setIsCoupon(checked === true)}
                />
                <Label htmlFor="is-coupon" className="cursor-pointer flex-1">
                  <div className="flex flex-col">
                    <span className="font-medium">Mark as Coupon/Promotion</span>
                    <span className="text-xs text-muted-foreground">
                      Check this if this is a free item, coupon, or promotional item. $0 coupon items will not affect historical price trends or averages.
                    </span>
                  </div>
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => goToNextUntrained(lineItemGroups)} disabled={saving}>
          Skip for Now
        </Button>
        <Button
          onClick={handleSave}
          disabled={
            saving ||
            !category ||
            (requiresRecipeCost && !purchaseQty) ||
            (requiresRecipeCost && !recipeUnit && !recipeUnitCustom)
          }
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Save & Categorize {similarCount > 1 ? `${similarCount} Items` : 'Item'}
            </>
          )}
        </Button>
      </div>

      {/* Navigation */}
      <div className="flex justify-between pt-4 border-t">
        <Button variant="outline" onClick={onPrevious}>
          ← Previous Step
        </Button>
        <Button onClick={onNext}>
          Continue
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    </div>
  );
};
